import React from "react";
import { AlertTriangle, RotateCcw, X } from "lucide-react";
import ErrorBoundary from "./ErrorBoundary";
import DraggablePanel from "./draggable-panels/DraggablePanel";

const ERROR_PANEL_POSITION = { x: 360, y: 88 };

function ErrorIcon() {
  return (
    <div className="shrink-0 rounded-md border border-red-400/70 bg-red-500/10 p-2 text-red-300">
      <AlertTriangle className="h-5 w-5" aria-hidden />
    </div>
  );
}

function RetryButton({ onClick, label = "Try again" }) {
  return (
    <button type="button" className="menu-button inline-flex items-center gap-2" onClick={onClick}>
      <RotateCcw className="h-4 w-4" aria-hidden />
      {label}
    </button>
  );
}

function CloseButton({ onClick, label = "Close" }) {
  return (
    <button type="button" className="menu-button inline-flex items-center gap-2" onClick={onClick}>
      <X className="h-4 w-4" aria-hidden />
      {label}
    </button>
  );
}

function BoundaryMessage({ title, body }) {
  return (
    <div className="flex min-w-0 items-start gap-3">
      <ErrorIcon />
      <div className="min-w-0">
        <div className="text-sm font-semibold text-gray-100">{title}</div>
        <div className="mt-1 text-sm leading-6 text-gray-300">{body}</div>
      </div>
    </div>
  );
}

export function AppRegionErrorBoundary({ name, title, children, resetKeys }) {
  return (
    <ErrorBoundary
      name={name}
      resetKeys={resetKeys}
      fallback={({ resetErrorBoundary }) => (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/45 p-4">
          <div
            className="menu-panel max-w-md p-4 text-gray-100 shadow-xl"
            role="alert"
            aria-live="assertive"
          >
            <BoundaryMessage
              title={`${title} stopped rendering`}
              body="The rest of the editor is still available. Retry this area after saving or adjusting the scene."
            />
            <div className="mt-4 flex justify-end">
              <RetryButton onClick={resetErrorBoundary} />
            </div>
          </div>
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}

export function PanelErrorBoundary({
  name,
  title,
  titleIcon,
  open = true,
  onClose,
  mobilePinPanelId,
  children,
  resetKeys,
}) {
  return (
    <ErrorBoundary
      name={name}
      resetKeys={resetKeys}
      fallback={({ resetErrorBoundary }) => (
        <DraggablePanel
          open={open}
          onClose={onClose}
          title={title}
          titleIcon={titleIcon}
          initialPosition={ERROR_PANEL_POSITION}
          mobilePinPanelId={mobilePinPanelId}
        >
          <div className="space-y-4 p-4 text-gray-100" role="alert" aria-live="assertive">
            <BoundaryMessage
              title={`${title} crashed`}
              body="This panel was isolated so the editor can keep running. Try again, or close the panel and continue working."
            />
            <div className="flex flex-wrap justify-end gap-2">
              <RetryButton onClick={resetErrorBoundary} />
              {onClose && <CloseButton onClick={onClose} />}
            </div>
          </div>
        </DraggablePanel>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}

export function ModalErrorBoundary({ name, title, onClose, children, resetKeys }) {
  return (
    <ErrorBoundary
      name={name}
      resetKeys={resetKeys}
      fallback={({ resetErrorBoundary }) => (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-4">
          <div
            className="w-full max-w-md rounded-lg border border-gray-700 p-4 text-gray-100 shadow-xl backdrop-blur-sm"
            style={{ backgroundColor: "var(--panel-surface)" }}
            role="alertdialog"
            aria-modal="true"
          >
            <BoundaryMessage
              title={`${title} crashed`}
              body="The rest of the editor is still available. Try reopening this dialog after saving your work."
            />
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <RetryButton onClick={resetErrorBoundary} />
              {onClose && <CloseButton onClick={onClose} />}
            </div>
          </div>
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}
